import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Edit2, Trash2, Plus, X, Layers, Info, Film, UploadCloud } from "lucide-react";
import Swal from 'sweetalert2';
import axios from "axios";
import { compressFilesBatch } from "../utils/imageCompressor";
import { API_BASE_URL } from "../config";

const MAX_FILE_SIZE_MB = 500; // 500MB per file limit
const MAX_BATCH_SIZE_MB = 1500; // 1500MB total batch upload limit
const MAX_BATCH_SIZE_BYTES = MAX_BATCH_SIZE_MB * 1024 * 1024;

const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export default function SubCategory() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("All");
  const [galleryItem, setGalleryItem] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState("");
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionStats, setCompressionStats] = useState(null);
  const [uploadedBytesInfo, setUploadedBytesInfo] = useState({ loaded: 0, total: 0 });

  const [formData, setFormData] = useState({
    title: "",
    categoryId: "",
    is_quick: 0,
    subcategory_image_hindi: [],
    subcategory_thumbnail_hindi: null,
    subcategory_image_english: [],
    subcategory_thumbnail_english: null
  });
  const [languages, setLanguages] = useState([]);

  const fetchCategories = () => {
    fetch(`${API_BASE_URL}/api/categories/all-categories`)
      .then(res => res.json())
      .then(resJson => {
        if (resJson && resJson.status && resJson.data) {
          setCategories(resJson.data);
          // Set default category for form
          if (resJson.data.length > 0) {
            setFormData(prev => ({ ...prev, categoryId: resJson.data[0].category_id_PK }));
          }
        }
      })
      .catch(err => console.error("Error fetching categories:", err));
  };

  const fetchSubCategories = () => {
    setLoading(true);
    const url = selectedCategoryFilter === "All"
      ? `${API_BASE_URL}/api/subcategories/all-subcategories`
      : `${API_BASE_URL}/api/subcategories/subcategories/${selectedCategoryFilter}`;

    fetch(url)
      .then(res => res.json())
      .then(resJson => {
        if (resJson && resJson.status && resJson.data) {
          const allSubCategories = [];
          if (selectedCategoryFilter === "All") {
            resJson.data.forEach((cat) => {
              const parentCategoryName = cat.category_name;
              const parentCategoryId = cat.category_id_PK;
              const parentCategoryImage = cat.category_image;
              if (cat.subcategories && Array.isArray(cat.subcategories)) {
                cat.subcategories.forEach((subGrouped) => {
                  const fallbackObj = subGrouped.Hindi || subGrouped.English || subGrouped.Default || {};
                  allSubCategories.push({
                    id: fallbackObj.subCategory_id_PK || `${subGrouped.subcategory_name}_temp`,
                    title: subGrouped.subcategory_name || "N/A",
                    Hindi: subGrouped.Hindi,
                    English: subGrouped.English,
                    Default: subGrouped.Default,
                    parentCategory: parentCategoryName || "N/A",
                    parentCategoryId: parentCategoryId,
                    parentCategoryImage: parentCategoryImage ? (parentCategoryImage.startsWith("http") ? parentCategoryImage : `${API_BASE_URL}/uploads/${parentCategoryImage}`) : "",
                    date: fallbackObj.updated_at
                      ? new Date(fallbackObj.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : "N/A",
                    subcategory_date: subGrouped.subcategory_date ? subGrouped.subcategory_date.split('T')[0] : "",
                    is_quick: subGrouped.is_quick || 0,
                    status: "Published"
                  });
                });
              }
            });
          } else {
            resJson.data.forEach((subGrouped) => {
              const fallbackObj = subGrouped.Hindi || subGrouped.English || subGrouped.Default || {};
              allSubCategories.push({
                id: fallbackObj.subCategory_id_PK || `${subGrouped.subcategory_name}_temp`,
                title: subGrouped.subcategory_name || "N/A",
                Hindi: subGrouped.Hindi,
                English: subGrouped.English,
                Default: subGrouped.Default,
                parentCategory: fallbackObj.category_name || subGrouped.category_name || "N/A",
                parentCategoryId: subGrouped.category_id_FK,
                parentCategoryImage: "",
                date: fallbackObj.updated_at
                  ? new Date(fallbackObj.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : "N/A",
                subcategory_date: subGrouped.subcategory_date ? subGrouped.subcategory_date.split('T')[0] : "",
                is_quick: subGrouped.is_quick || 0,
                status: "Published"
              });
            });
          }
          setSubCategories(allSubCategories);
        } else {
          setSubCategories([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching subcategories:", err);
        setSubCategories([]);
        setLoading(false);
      });
  };

  const fetchLanguages = () => {
    fetch(`${API_BASE_URL}/api/languages/all`)
      .then(res => res.json())
      .then(resJson => {
        if (resJson && resJson.status && resJson.data) {
          setLanguages(resJson.data);
        }
      })
      .catch(err => console.error("Error fetching languages:", err));
  };

  useEffect(() => {
    fetchCategories();
    fetchLanguages();
  }, []);

  useEffect(() => {
    fetchSubCategories();
  }, [selectedCategoryFilter]);

  // Calculate file sizes
  const hindiImagesBytes = (formData.subcategory_image_hindi || []).reduce((acc, f) => acc + (f.size || 0), 0);
  const hindiThumbBytes = formData.subcategory_thumbnail_hindi?.size || 0;
  const hindiTotalBytes = hindiImagesBytes + hindiThumbBytes;

  const englishImagesBytes = (formData.subcategory_image_english || []).reduce((acc, f) => acc + (f.size || 0), 0);
  const englishThumbBytes = formData.subcategory_thumbnail_english?.size || 0;
  const englishTotalBytes = englishImagesBytes + englishThumbBytes;

  const grandTotalBytes = hindiTotalBytes + englishTotalBytes;

  const handleSaveSubCategory = async () => {
    if (!formData.title.trim()) {
      Swal.fire("Error", "Sub-Category Title is required!", "error");
      return;
    }

    const targetCategoryId = formData.categoryId || (categories.length > 0 ? categories[0].category_id_PK : null);
    if (!targetCategoryId) {
      Swal.fire("Error", "Please select a Parent Category!", "error");
      return;
    }

    if (!editingSubCategoryId && (!formData.subcategory_image_hindi.length && !formData.subcategory_image_english.length)) {
      Swal.fire("Error", "At least one Sub-Category Image or Video (Hindi or English) is required!", "error");
      return;
    }

    if (grandTotalBytes > MAX_BATCH_SIZE_BYTES) {
      Swal.fire(
        "Error",
        `Total upload size exceeds the maximum limit of ${MAX_BATCH_SIZE_MB} MB. (Your upload size: ${formatBytes(grandTotalBytes)})`,
        "error"
      );
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setCompressionStats(null);
    setCompressing(true);
    setCompressionProgress(0);
    setUploadStatusText("Compressing images...");

    const hindiFiles = formData.subcategory_image_hindi ? Array.from(formData.subcategory_image_hindi) : [];
    const englishFiles = formData.subcategory_image_english ? Array.from(formData.subcategory_image_english) : [];
    const hindiThumb = formData.subcategory_thumbnail_hindi;
    const englishThumb = formData.subcategory_thumbnail_english;

    const itemsToCompress = [
        ...hindiFiles.map(f => ({ file: f, category: 'hindi_img' })),
        ...englishFiles.map(f => ({ file: f, category: 'english_img' })),
        ...(hindiThumb ? [{ file: hindiThumb, category: 'hindi_thumb' }] : []),
        ...(englishThumb ? [{ file: englishThumb, category: 'english_thumb' }] : [])
    ];

    let finalItems = itemsToCompress;
    let compStats = null;
    if (itemsToCompress.length > 0) {
        const result = await compressFilesBatch(itemsToCompress, (pct) => {
            setCompressionProgress(pct);
        });
        finalItems = result.compressedItems;
        compStats = result.stats;
        setCompressionStats(result.stats);
    }
    setCompressing(false);
    setUploadStatusText("Uploading files to server...");

    const effectiveTotal = compStats?.compressedTotal || grandTotalBytes;
    setUploadedBytesInfo({ loaded: 0, total: effectiveTotal });

    const savedStatsBadge = (compStats && compStats.savedPercent > 0)
        ? `<div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #059669; font-weight: 600; display: flex; justify-content: space-between;"><span>⚡ Compressed: <span style="text-decoration: line-through; color: #94a3b8;">${formatBytes(compStats.originalTotal)}</span> ➔ ${formatBytes(compStats.compressedTotal)}</span><span style="background: #d1fae5; color: #065f46; padding: 2px 6px; border-radius: 4px;">Saved ${compStats.savedPercent}%</span></div>`
        : '';

    Swal.fire({
      title: editingSubCategoryId ? "Updating Sub-Category..." : "Uploading Sub-Category Media...",
      html: `
        <div style="margin-top: 10px; text-align: left;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
            <span id="swal-upload-status" style="font-size:13px; font-weight:600; color:#475569;">Uploading files...</span>
            <span id="swal-upload-pct" style="font-size:18px; font-weight:800; color:#2563eb;">0%</span>
          </div>
          <div style="background:#e2e8f0; border-radius:9999px; height:16px; overflow:hidden; width:100%;">
            <div id="swal-upload-bar" style="background:linear-gradient(90deg, #3b82f6, #06b6d4); width:0%; height:100%; border-radius:9999px; transition:width 0.15s ease;"></div>
          </div>
          <div style="display:flex; justify-content:space-between; margin-top:10px; font-size:12px; color:#64748b;">
            <span id="swal-upload-bytes">0 B / ${formatBytes(effectiveTotal)}</span>
            <span>Please keep window open</span>
          </div>
          ${savedStatsBadge}
        </div>
      `,
      allowOutsideClick: false,
      showConfirmButton: false
    });

    const formDataObj = new FormData();
    formDataObj.append("subcategory_name", formData.title);
    formDataObj.append("category_id_FK", targetCategoryId);
    formDataObj.append("is_quick", formData.is_quick ? 1 : 0);

    finalItems.forEach(it => {
        if (it.category === 'hindi_img') formDataObj.append("subcategory_image_hindi", it.file);
        else if (it.category === 'english_img') formDataObj.append("subcategory_image_english", it.file);
        else if (it.category === 'hindi_thumb') formDataObj.append("subcategory_thumbnail_hindi", it.file);
        else if (it.category === 'english_thumb') formDataObj.append("subcategory_thumbnail_english", it.file);
    });

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const rawPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Scale network transfer to 0% - 90%
          const scaledPercent = Math.min(90, Math.round((progressEvent.loaded * 90) / progressEvent.total));
          setUploadProgress(scaledPercent);
          setUploadedBytesInfo({ loaded: progressEvent.loaded, total: progressEvent.total });

          const bar = document.getElementById('swal-upload-bar');
          const pct = document.getElementById('swal-upload-pct');
          const bytes = document.getElementById('swal-upload-bytes');
          const status = document.getElementById('swal-upload-status');

          if (bytes) {
            bytes.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;
          }

          if (rawPercent < 100) {
            if (bar) bar.style.width = scaledPercent + '%';
            if (pct) pct.innerText = scaledPercent + '%';
            if (status) status.innerText = `Uploading to server: ${scaledPercent}%...`;
          } else {
            // Bytes transferred to server; now backend is compressing & uploading to DO Spaces
            if (bar) {
              bar.style.width = '95%';
              bar.style.background = 'linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)';
            }
            if (pct) pct.innerText = 'Saving...';
            if (status) {
              status.innerHTML = '<span style="display:inline-flex; align-items:center; gap:6px;"><span class="inline-block w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span> <strong>Optimizing & saving to Cloud...</strong></span>';
            }
            setUploadStatusText("Optimizing & saving to Cloud...");
          }
        }
      }
    };

    try {
      let res;
      if (editingSubCategoryId) {
        res = await axios.put(`${API_BASE_URL}/api/subcategories/update-subcategory/${editingSubCategoryId}`, formDataObj, config);
      } else {
        res = await axios.post(`${API_BASE_URL}/api/subcategories/create-subcategory`, formDataObj, config);
      }

      // Show 100% complete briefly before closing
      const bar = document.getElementById('swal-upload-bar');
      const pct = document.getElementById('swal-upload-pct');
      const status = document.getElementById('swal-upload-status');
      if (bar) {
        bar.style.width = '100%';
        bar.style.background = '#10b981';
      }
      if (pct) pct.innerText = '100%';
      if (status) status.innerHTML = '<span style="color:#10b981; font-weight:700;">✅ Upload complete!</span>';
      setUploadProgress(100);

      await new Promise(resolve => setTimeout(resolve, 350));

      Swal.close();
      setCompressing(false);
      setIsUploading(false);
      setUploadProgress(0);

      if (res.data && res.data.status) {
        let successMsg = editingSubCategoryId ? "Sub-Category updated successfully!" : "Sub-Category created successfully!";
        if (compStats && compStats.savedPercent > 0) {
          successMsg += ` (⚡ Compressed: ${formatBytes(compStats.originalTotal)} ➔ ${formatBytes(compStats.compressedTotal)}, Saved ${compStats.savedPercent}%)`;
        }
        Swal.fire("Success", successMsg, "success");
        setFormData({
          title: "",
          categoryId: categories[0]?.category_id_PK || "",
          is_quick: 0,
          subcategory_image_hindi: [],
          subcategory_thumbnail_hindi: null,
          subcategory_image_english: [],
          subcategory_thumbnail_english: null
        });
        setEditingSubCategoryId(null);
        setIsModalOpen(false);
        fetchSubCategories();
      } else {
        throw new Error(res.data?.message || "Failed to save sub-category");
      }
    } catch (err) {
      Swal.close();
      setCompressing(false);
      setIsUploading(false);
      setUploadProgress(0);
      console.error("Save subcategory error:", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to save sub-category";
      Swal.fire("Error", errMsg, "error");
    }
  };

  const handleEditSubCategory = (subCat) => {
    setFormData({
      title: subCat.title,
      categoryId: subCat.parentCategoryId || (categories[0]?.category_id_PK || ""),
      is_quick: subCat.is_quick || 0,
      subcategory_image_hindi: [],
      subcategory_thumbnail_hindi: null,
      subcategory_image_english: [],
      subcategory_thumbnail_english: null
    });
    // Find ID since it could be Hindi's or English's
    const firstId = subCat.Hindi?.subCategory_id_PK || subCat.English?.subCategory_id_PK || subCat.Default?.subCategory_id_PK || subCat.id;
    setEditingSubCategoryId(firstId);
    setIsUploading(false);
    setUploadProgress(0);
    setIsModalOpen(true);
  };

  const handleDeleteSubCategory = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while the sub-category is deleted.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        fetch(`${API_BASE_URL}/api/subcategories/delete-subcategory/${id}`, {
          method: "DELETE"
        })
          .then(res => {
            if (!res.ok) throw new Error("Failed to delete sub-category");
            return res.json();
          })
          .then(resJson => {
            Swal.close();
            if (resJson && resJson.status) {
              Swal.fire('Deleted!', 'Sub-Category has been deleted.', 'success');
              fetchSubCategories(); // Reload the data from backend
            } else {
              throw new Error(resJson.message || "Failed to delete sub-category");
            }
          })
          .catch(err => {
            Swal.close();
            console.error("Delete subcategory error:", err);
            Swal.fire("Error", err.message, "error");
          });
      }
    });
  };

  const handleDeleteGalleryImage = (imageUrl) => {
    const filename = imageUrl.split('/').pop().trim();
    if (!filename) return;

    Swal.fire({
      title: 'Delete this Media?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        fetch(`${API_BASE_URL}/api/subcategories/delete-image/${galleryItem.id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageName: filename })
        })
          .then(res => res.json())
          .then(resJson => {
            Swal.close();
            if (resJson.status) {
              Swal.fire('Deleted!', 'Media has been removed.', 'success');
              const newUrls = (galleryItem.image || "").split(',').filter(u => u && u.trim() !== imageUrl);
              if (newUrls.length === 0) {
                setGalleryItem(null);
              } else {
                setGalleryItem({ ...galleryItem, image: newUrls.join(',') });
              }
              fetchSubCategories();
            } else {
              Swal.fire('Error', resJson.message || 'Failed to delete media', 'error');
            }
          })
          .catch(err => {
            Swal.close();
            console.error(err);
            Swal.fire('Error', err.message, 'error');
          });
      }
    });
  };

  const [replacingImageUrl, setReplacingImageUrl] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleEditGalleryImageClick = (url) => {
    setReplacingImageUrl(url);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChangeForReplace = async (e) => {
    const file = e.target.files[0];
    if (!file || !replacingImageUrl) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      Swal.fire("Warning", `Selected file exceeds ${MAX_FILE_SIZE_MB} MB limit!`, "warning");
      e.target.value = "";
      return;
    }

    const oldFilename = replacingImageUrl.split('/').pop().trim();
    if (!oldFilename) return;

    Swal.fire({
      title: "Replacing Media...",
      html: `
        <div style="margin-top: 10px; text-align: left;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
            <span id="swal-rep-status" style="font-size:13px; font-weight:600; color:#475569;">Uploading new file...</span>
            <span id="swal-rep-pct" style="font-size:16px; font-weight:800; color:#2563eb;">0%</span>
          </div>
          <div style="background:#e2e8f0; border-radius:9999px; height:14px; overflow:hidden; width:100%;">
            <div id="swal-rep-bar" style="background:linear-gradient(90deg, #3b82f6, #06b6d4); width:0%; height:100%; border-radius:9999px; transition:width 0.15s ease;"></div>
          </div>
          <div style="display:flex; justify-content:space-between; margin-top:8px; font-size:12px; color:#64748b;">
            <span id="swal-rep-bytes">0 B / ${formatBytes(file.size)}</span>
            <span style="max-width:150px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${file.name}</span>
          </div>
        </div>
      `,
      allowOutsideClick: false,
      showConfirmButton: false
    });

    const formData = new FormData();
    formData.append("oldImageName", oldFilename);
    formData.append("new_image", file);

    try {
      const res = await axios.put(`${API_BASE_URL}/api/subcategories/replace-image/${galleryItem.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const rawPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const scaledPercent = Math.min(90, Math.round((progressEvent.loaded * 90) / progressEvent.total));
            const bar = document.getElementById('swal-rep-bar');
            const pct = document.getElementById('swal-rep-pct');
            const bytes = document.getElementById('swal-rep-bytes');
            const status = document.getElementById('swal-rep-status');

            if (bytes) bytes.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;

            if (rawPercent < 100) {
              if (bar) bar.style.width = scaledPercent + '%';
              if (pct) pct.innerText = scaledPercent + '%';
              if (status) status.innerText = `Uploading: ${scaledPercent}%...`;
            } else {
              if (bar) {
                bar.style.width = '95%';
                bar.style.background = 'linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)';
              }
              if (pct) pct.innerText = 'Saving...';
              if (status) {
                status.innerHTML = '<span style="display:inline-flex; align-items:center; gap:6px;"><span class="inline-block w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span> <strong>Optimizing & replacing on Cloud...</strong></span>';
              }
            }
          }
        }
      });

      const bar = document.getElementById('swal-rep-bar');
      const pct = document.getElementById('swal-rep-pct');
      const status = document.getElementById('swal-rep-status');
      if (bar) {
        bar.style.width = '100%';
        bar.style.background = '#10b981';
      }
      if (pct) pct.innerText = '100%';
      if (status) status.innerHTML = '<span style="color:#10b981; font-weight:700;">✅ Replaced successfully!</span>';

      await new Promise(resolve => setTimeout(resolve, 350));

      Swal.close();
      if (res.data && res.data.status) {
        Swal.fire("Success", "Media replaced successfully!", "success");
        if (res.data.new_image_url) {
          const newUrl = res.data.new_image_url;
          const updatedImages = (galleryItem.image || "").split(',').map(f => f.includes(oldFilename) ? newUrl : f).join(',');
          setGalleryItem({ ...galleryItem, image: updatedImages });
        }
        fetchSubCategories();
      } else {
        Swal.fire("Error", res.data?.message || "Failed to replace media", "error");
      }
    } catch (err) {
      Swal.close();
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    } finally {
      setReplacingImageUrl(null);
      e.target.value = "";
    }
  };

  const addMediaInputRef = React.useRef(null);

  const handleGalleryAddMedia = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !galleryItem) return;

    const totalBytes = files.reduce((acc, f) => acc + f.size, 0);

    Swal.fire({
      title: "Uploading Media to Sub-Category...",
      html: `
        <div style="margin-top: 10px; text-align: left;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
            <span id="swal-add-status" style="font-size:13px; font-weight:600; color:#475569;">Uploading files...</span>
            <span id="swal-add-pct" style="font-size:18px; font-weight:800; color:#2563eb;">0%</span>
          </div>
          <div style="background:#e2e8f0; border-radius:9999px; height:16px; overflow:hidden; width:100%;">
            <div id="swal-add-bar" style="background:linear-gradient(90deg, #3b82f6, #06b6d4); width:0%; height:100%; border-radius:9999px; transition:width 0.15s ease;"></div>
          </div>
          <div style="display:flex; justify-content:space-between; margin-top:10px; font-size:12px; color:#64748b;">
            <span id="swal-add-bytes">0 B / ${formatBytes(totalBytes)}</span>
            <span>${files.length} file(s) selected</span>
          </div>
        </div>
      `,
      allowOutsideClick: false,
      showConfirmButton: false
    });

    const formData = new FormData();
    files.forEach(f => formData.append("image", f));

    try {
      const res = await axios.put(`${API_BASE_URL}/api/subcategories/add-image/${galleryItem.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const rawPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const scaledPercent = Math.min(90, Math.round((progressEvent.loaded * 90) / progressEvent.total));
            const bar = document.getElementById('swal-add-bar');
            const pct = document.getElementById('swal-add-pct');
            const bytes = document.getElementById('swal-add-bytes');
            const status = document.getElementById('swal-add-status');

            if (bytes) bytes.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;

            if (rawPercent < 100) {
              if (bar) bar.style.width = scaledPercent + '%';
              if (pct) pct.innerText = scaledPercent + '%';
              if (status) status.innerText = `Uploading: ${scaledPercent}%...`;
            } else {
              if (bar) {
                bar.style.width = '95%';
                bar.style.background = 'linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)';
              }
              if (pct) pct.innerText = 'Saving...';
              if (status) {
                status.innerHTML = '<span style="display:inline-flex; align-items:center; gap:6px;"><span class="inline-block w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span> <strong>Optimizing & saving to Cloud...</strong></span>';
              }
            }
          }
        }
      });

      const bar = document.getElementById('swal-add-bar');
      const pct = document.getElementById('swal-add-pct');
      const status = document.getElementById('swal-add-status');
      if (bar) {
        bar.style.width = '100%';
        bar.style.background = '#10b981';
      }
      if (pct) pct.innerText = '100%';
      if (status) status.innerHTML = '<span style="color:#10b981; font-weight:700;">✅ Upload complete!</span>';

      await new Promise(resolve => setTimeout(resolve, 350));

      Swal.close();
      if (res.data && res.data.status) {
        Swal.fire("Success", `${files.length} media item(s) added successfully!`, "success");
        fetchSubCategories();
        setGalleryItem(null);
      } else {
        Swal.fire("Error", res.data?.message || "Failed to add media", "error");
      }
    } catch (err) {
      Swal.close();
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message || "Failed to add media", "error");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <>
      <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
          <Header
            onMenuClick={() => setIsSidebarOpen(true)}
            title="Sub-Category Management"
            subtitle="Manage your App's Sub-Categories and parent assignments."
          />

          <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
            {/* Header Section */}
            <div className="mb-6 sm:mb-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>


              </div>
              <div className="flex items-center gap-3 w-full lg:w-auto">
                <button
                  onClick={() => {
                    setFormData({
                      title: "",
                      categoryId: categories[0]?.category_id_PK || "",
                      is_quick: 0,
                      subcategory_image_hindi: [],
                      subcategory_thumbnail_hindi: null,
                      subcategory_image_english: [],
                      subcategory_thumbnail_english: null
                    });
                    setEditingSubCategoryId(null);
                    setIsUploading(false);
                    setUploadProgress(0);
                    setIsModalOpen(true);
                  }}
                  className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2 rounded-xl text-sm font-medium hover:-translate-y-0.5 transition-all flex items-center gap-2">
                  <Plus size={16} />
                  Create Sub-Category
                </button>
              </div>
            </div>

            {/* Main Card */}
            <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1">
              {/* Toolbar */}
              <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-600 rounded-xl px-3 py-2 outline-none focus:border-blue-500 shadow-sm cursor-pointer w-full sm:w-48 bg-white dark:bg-slate-800"
                  >
                    <option value="All">All Categories</option>
                    {categories.map(c => (
                      <option key={c.category_id_PK} value={c.category_id_PK}>
                        {c.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative w-full sm:w-72">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search sub-categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm placeholder-slate-400"
                  />
                </div>
              </div>

              {/* Table Container */}
              <div className="overflow-x-auto flex-1">
                <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                  <thead className="bg-slate-50/50 dark:bg-slate-800/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700/50">
                    <tr>
                      <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 dark:bg-slate-900/50 z-20 shadow-[1px_0_0_rgba(0,0,0,0.05)] dark:shadow-[1px_0_0_rgba(0,0,0,0.5)]">Sub-Category</th>
                      <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Category</th>
                      <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Created Date</th>
                      <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right sticky right-0 bg-slate-50 dark:bg-slate-900/50 z-10">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/50 dark:divide-slate-700/50">
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="p-8 text-center text-slate-500 dark:text-slate-400 font-medium">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            Loading sub-categories...
                          </div>
                        </td>
                      </tr>
                    ) : subCategories.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="p-8 text-center text-slate-500 dark:text-slate-400 font-medium">
                          No sub-categories found
                        </td>
                      </tr>
                    ) : (
                      subCategories
                        .filter((item) => item.title.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((item) => {
                          const hindiMediaStr = item.Hindi?.subcategory_image;
                          const englishMediaStr = item.English?.subcategory_image;
                          const defaultMediaStr = item.Default?.subcategory_image;

                          const renderMedia = (mediaStr, thumbStr, bgClass, name) => {
                            if (!mediaStr && !thumbStr) return null;
                            const firstMedia = mediaStr ? mediaStr.split(',')[0].trim() : null;
                            const safeThumb = thumbStr ? (thumbStr.startsWith("http") ? thumbStr : `${API_BASE_URL}/uploads/${thumbStr}`) : null;
                            const safeMedia = firstMedia ? (firstMedia.startsWith("http") ? firstMedia : `${API_BASE_URL}/uploads/${firstMedia}`) : null;
                            const isVideo = firstMedia && firstMedia.match(/\.(mp4|webm|avi|mpeg|mov|mkv)$/i);

                            return (
                              <div className="relative group cursor-pointer" title={name} onClick={() => setGalleryItem({ ...item, image: mediaStr })}>
                                <div className={`absolute inset-0 border-2 rounded-lg ${bgClass}`}></div>
                                {isVideo ? (
                                  <video src={safeMedia} className="w-12 h-12 rounded-lg object-cover" muted loop playsInline autoPlay />
                                ) : (
                                  <img src={safeThumb || safeMedia} alt={name} className="w-12 h-12 rounded-lg object-cover" />
                                )}
                                <span className={`absolute -bottom-2 -right-2 text-[10px] px-1.5 py-0.5 rounded-md font-bold text-white shadow-sm ${bgClass.replace('border-', 'bg-')}`}>
                                  {name.charAt(0)}
                                </span>
                              </div>
                            );
                          };

                          return (
                            <tr key={item.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/80 transition-colors group">
                              <td className="p-4 sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/50 z-10 shadow-[1px_0_0_rgba(0,0,0,0.05)] dark:shadow-[1px_0_0_rgba(0,0,0,0.5)] transition-colors">
                                <div className="flex items-center gap-3">
                                  {/* Thumbnails Map */}
                                  <div className="flex gap-2">
                                    {renderMedia(hindiMediaStr, item.Hindi?.subcategory_thumbnail, 'border-blue-500', 'Hindi')}
                                    {renderMedia(englishMediaStr, item.English?.subcategory_thumbnail, 'border-emerald-500', 'English')}
                                    {!hindiMediaStr && !englishMediaStr && renderMedia(defaultMediaStr, item.Default?.subcategory_thumbnail, 'border-slate-400', 'Default')}
                                  </div>
                                  <span className="font-bold text-slate-800 dark:text-slate-100">{item.title}</span>
                                  {Boolean(item.is_quick == 1 || item.is_quick === "1") && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded-full shadow-xs">
                                      🚀 Quick Mode
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  {item.parentCategoryImage ? (
                                    <img src={item.parentCategoryImage} alt={item.parentCategory} className="w-6 h-6 rounded-lg object-cover border border-slate-200 dark:border-slate-700" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center"><Layers size={12} /></div>
                                  )}
                                  <span className="text-sm text-slate-600 font-medium">{item.parentCategory}</span>
                                </div>
                              </td>

                              <td className="p-4 text-sm text-slate-600 font-medium">{item.date}</td>

                              <td className="p-4 text-right sticky right-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/50 z-10 shadow-[-1px_0_0_rgba(0,0,0,0.05)] dark:shadow-[-1px_0_0_rgba(0,0,0,0.5)] transition-colors">
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => handleEditSubCategory(item)} title="Edit" className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                                    <Edit2 size={18} />
                                  </button>
                                  <button onClick={() => handleDeleteSubCategory(item.id)} title="Delete" className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {
        isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh] relative z-10 overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100 dark:border-slate-700 shrink-0">
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{editingSubCategoryId ? 'Edit Sub-Category' : 'Create New Sub-Category'}</h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-5 flex-1 overflow-y-auto">
                {/* Max Upload Size & Format Information Banner */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800/80 border border-blue-200/80 dark:border-blue-900/50 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-600/10 text-blue-600 dark:text-blue-400 rounded-xl shrink-0 mt-0.5">
                      <Info size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">Upload Size Limit:</span>
                        <span className="bg-blue-600 text-white font-bold px-2 py-0.5 rounded-md text-[11px] shadow-sm">
                          Max {MAX_FILE_SIZE_MB} MB / file
                        </span>
                        <span className="text-slate-500 dark:text-slate-400 font-medium">
                          (Up to {MAX_BATCH_SIZE_MB} MB total per batch)
                        </span>
                      </div>
                      <p className="text-slate-500 dark:text-slate-400 mt-1">
                        Supported: <span className="font-semibold text-slate-700 dark:text-slate-300">JPG, PNG, WEBP, GIF, SVG, BMP, MP4, WEBM, MOV</span> (High quality images & videos)
                      </p>
                    </div>
                  </div>
                  {grandTotalBytes > 0 && (
                    <div className="flex items-center gap-2 bg-white dark:bg-slate-900 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm shrink-0">
                      <UploadCloud size={16} className="text-blue-600 dark:text-blue-400" />
                      <div className="text-right">
                        <span className="text-slate-400 text-[10px] block">Selected Media</span>
                        <span className={`font-bold text-xs ${grandTotalBytes > MAX_BATCH_SIZE_BYTES ? 'text-red-500' : 'text-blue-600 dark:text-blue-400'}`}>
                          {formatBytes(grandTotalBytes)}
                        </span>
                        <span className="text-slate-400 text-[10px]"> / {MAX_BATCH_SIZE_MB} MB</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Sub-Category Title *</label>
                    <input
                      type="text"
                      placeholder="e.g. Festival Calendar"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      disabled={isUploading}
                      className="w-full bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder-slate-400 disabled:opacity-60"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Parent Category *</label>
                    <select
                      value={formData.categoryId}
                      onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                      disabled={isUploading}
                      className="w-full bg-slate-50 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 disabled:opacity-60"
                    >
                      {categories.map(c => (
                        <option key={c.category_id_PK} value={c.category_id_PK}>
                          {c.category_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-row items-center cursor-pointer p-3 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-xl" onClick={() => setFormData({ ...formData, is_quick: formData.is_quick ? 0 : 1 })}>
                  <input
                    type="checkbox"
                    checked={Boolean(formData.is_quick == 1 || formData.is_quick === "1" || formData.is_quick === true)}
                    onChange={(e) => setFormData({ ...formData, is_quick: e.target.checked ? 1 : 0 })}
                    disabled={isUploading}
                    className="w-4 h-4 cursor-pointer text-indigo-600 bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 rounded focus:ring-indigo-500"
                  />
                  <label className="ml-2.5 block text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 cursor-pointer select-none">
                    🚀 Enable Quick Sub-Category Assignment <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400 block sm:inline sm:ml-1">(Auto-assigns images & videos ending with A/B/C/D to user groups)</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* --- HINDI UPLOAD SECTION --- */}
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                    <div className="flex items-center justify-between pb-1 border-b border-slate-200/60 dark:border-slate-700/60">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                        Hindi Variant
                      </h4>
                      {hindiTotalBytes > 0 && (
                        <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-100/60 dark:bg-blue-900/40 px-2 py-0.5 rounded-md">
                          {formData.subcategory_image_hindi.length} file{formData.subcategory_image_hindi.length !== 1 ? 's' : ''} • {formatBytes(hindiTotalBytes)}
                        </span>
                      )}
                    </div>

                    {/* Hindi Category Images/Videos (Multiple) */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Images / Videos (Multiple)
                        </label>
                        <span className="text-[11px] text-slate-400">Up to {MAX_FILE_SIZE_MB}MB per file</span>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        disabled={isUploading}
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          const oversized = files.filter(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
                          if (oversized.length > 0) {
                            Swal.fire({
                              icon: 'warning',
                              title: 'File Exceeds Size Limit',
                              html: `The following file(s) exceed the <b>${MAX_FILE_SIZE_MB} MB</b> limit:<br/><ul style="text-align:left;margin-top:6px;font-size:13px;">${oversized.map(f => `<li>${f.name} (${formatBytes(f.size)})</li>`).join('')}</ul>`
                            });
                            e.target.value = "";
                            return;
                          }
                          setFormData(prev => ({ ...prev, subcategory_image_hindi: [...prev.subcategory_image_hindi, ...files] }));
                          e.target.value = "";
                        }}
                        className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs outline-none focus:border-blue-500 transition-all bg-white dark:bg-slate-800 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer disabled:opacity-60"
                      />
                      {formData.subcategory_image_hindi.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2.5 max-h-48 overflow-y-auto p-1">
                          {formData.subcategory_image_hindi.map((file, index) => {
                            const isVid = file.type ? file.type.startsWith('video/') : file.name.match(/\.(mp4|webm|avi|mpeg|mov|mkv)$/i);
                            return (
                              <div key={index} className="relative w-20 h-20 group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900 shadow-sm shrink-0" title={`${file.name} (${formatBytes(file.size)})`}>
                                {isVid ? (
                                  <video src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                                ) : (
                                  <img src={URL.createObjectURL(file)} alt="preview" className="w-full h-full object-cover" />
                                )}
                                <span className="absolute bottom-1 left-1 bg-black/80 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded font-mono font-medium">
                                  {formatBytes(file.size)}
                                </span>
                                {isVid && (
                                  <span className="absolute top-1 left-1 bg-blue-600 text-white p-1 rounded-md text-[9px]">
                                    <Film size={11} />
                                  </span>
                                )}
                                {!isUploading && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, subcategory_image_hindi: prev.subcategory_image_hindi.filter((_, i) => i !== index) }))}
                                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                    title="Remove"
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Hindi Thumbnail */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Thumbnail (Optional)
                        </label>
                        <span className="text-[11px] text-slate-400">Image format</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={isUploading}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                            Swal.fire('Warning', `Thumbnail exceeds ${MAX_FILE_SIZE_MB}MB limit`, 'warning');
                            e.target.value = "";
                            return;
                          }
                          setFormData(prev => ({ ...prev, subcategory_thumbnail_hindi: file || null }));
                        }}
                        className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs outline-none focus:border-blue-500 transition-all bg-white dark:bg-slate-800 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer disabled:opacity-60"
                        id="thumbnailInputHindi"
                      />
                      {formData.subcategory_thumbnail_hindi && formData.subcategory_thumbnail_hindi instanceof File && (
                        <div className="mt-3 relative w-20 h-20 group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm bg-slate-900" title={`${formData.subcategory_thumbnail_hindi.name} (${formatBytes(formData.subcategory_thumbnail_hindi.size)})`}>
                          <img src={URL.createObjectURL(formData.subcategory_thumbnail_hindi)} alt="thumbnail preview" className="w-full h-full object-cover" />
                          <span className="absolute bottom-1 left-1 bg-black/80 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded font-mono font-medium">
                            {formatBytes(formData.subcategory_thumbnail_hindi.size)}
                          </span>
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, subcategory_thumbnail_hindi: null }));
                                const el = document.getElementById('thumbnailInputHindi');
                                if (el) el.value = '';
                              }}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* --- ENGLISH UPLOAD SECTION --- */}
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                    <div className="flex items-center justify-between pb-1 border-b border-slate-200/60 dark:border-slate-700/60">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                        English Variant
                      </h4>
                      {englishTotalBytes > 0 && (
                        <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-100/60 dark:bg-emerald-900/40 px-2 py-0.5 rounded-md">
                          {formData.subcategory_image_english.length} file{formData.subcategory_image_english.length !== 1 ? 's' : ''} • {formatBytes(englishTotalBytes)}
                        </span>
                      )}
                    </div>

                    {/* English Category Images/Videos (Multiple) */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Images / Videos (Multiple)
                        </label>
                        <span className="text-[11px] text-slate-400">Up to {MAX_FILE_SIZE_MB}MB per file</span>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        disabled={isUploading}
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          const oversized = files.filter(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
                          if (oversized.length > 0) {
                            Swal.fire({
                              icon: 'warning',
                              title: 'File Exceeds Size Limit',
                              html: `The following file(s) exceed the <b>${MAX_FILE_SIZE_MB} MB</b> limit:<br/><ul style="text-align:left;margin-top:6px;font-size:13px;">${oversized.map(f => `<li>${f.name} (${formatBytes(f.size)})</li>`).join('')}</ul>`
                            });
                            e.target.value = "";
                            return;
                          }
                          setFormData(prev => ({ ...prev, subcategory_image_english: [...prev.subcategory_image_english, ...files] }));
                          e.target.value = "";
                        }}
                        className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs outline-none focus:border-blue-500 transition-all bg-white dark:bg-slate-800 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer disabled:opacity-60"
                      />
                      {formData.subcategory_image_english.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2.5 max-h-48 overflow-y-auto p-1">
                          {formData.subcategory_image_english.map((file, index) => {
                            const isVid = file.type ? file.type.startsWith('video/') : file.name.match(/\.(mp4|webm|avi|mpeg|mov|mkv)$/i);
                            return (
                              <div key={index} className="relative w-20 h-20 group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900 shadow-sm shrink-0" title={`${file.name} (${formatBytes(file.size)})`}>
                                {isVid ? (
                                  <video src={URL.createObjectURL(file)} className="w-full h-full object-cover" />
                                ) : (
                                  <img src={URL.createObjectURL(file)} alt="preview" className="w-full h-full object-cover" />
                                )}
                                <span className="absolute bottom-1 left-1 bg-black/80 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded font-mono font-medium">
                                  {formatBytes(file.size)}
                                </span>
                                {isVid && (
                                  <span className="absolute top-1 left-1 bg-emerald-600 text-white p-1 rounded-md text-[9px]">
                                    <Film size={11} />
                                  </span>
                                )}
                                {!isUploading && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, subcategory_image_english: prev.subcategory_image_english.filter((_, i) => i !== index) }))}
                                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                    title="Remove"
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* English Thumbnail */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Thumbnail (Optional)
                        </label>
                        <span className="text-[11px] text-slate-400">Image format</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={isUploading}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                            Swal.fire('Warning', `Thumbnail exceeds ${MAX_FILE_SIZE_MB}MB limit`, 'warning');
                            e.target.value = "";
                            return;
                          }
                          setFormData(prev => ({ ...prev, subcategory_thumbnail_english: file || null }));
                        }}
                        className="w-full border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs outline-none focus:border-blue-500 transition-all bg-white dark:bg-slate-800 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer disabled:opacity-60"
                        id="thumbnailInputEnglish"
                      />
                      {formData.subcategory_thumbnail_english && formData.subcategory_thumbnail_english instanceof File && (
                        <div className="mt-3 relative w-20 h-20 group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm bg-slate-900" title={`${formData.subcategory_thumbnail_english.name} (${formatBytes(formData.subcategory_thumbnail_english.size)})`}>
                          <img src={URL.createObjectURL(formData.subcategory_thumbnail_english)} alt="thumbnail preview" className="w-full h-full object-cover" />
                          <span className="absolute bottom-1 left-1 bg-black/80 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded font-mono font-medium">
                            {formatBytes(formData.subcategory_thumbnail_english.size)}
                          </span>
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, subcategory_thumbnail_english: null }));
                                const el = document.getElementById('thumbnailInputEnglish');
                                if (el) el.value = '';
                              }}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                </div>

                {/* Real-time In-Modal Upload Progress Bar */}
                {isUploading && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800/90 border border-blue-200 dark:border-blue-900 rounded-2xl p-4 space-y-2.5 animate-in fade-in duration-200 shadow-sm">
                    <div className="flex items-center justify-between text-xs font-semibold text-blue-900 dark:text-blue-200">
                      <span className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full animate-pulse ${compressing ? 'bg-amber-500' : 'bg-blue-600'}`}></div>
                        {compressing
                          ? `⚡ Compressing Images: ${compressionProgress}%`
                          : uploadStatusText || (uploadProgress >= 100 ? "Processing on cloud storage..." : `Uploading: ${uploadProgress}%`)}
                      </span>
                      <span className={`font-bold text-sm ${compressing ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`}>
                        {compressing ? `${compressionProgress}%` : `${uploadProgress}%`}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 h-3 rounded-full overflow-hidden shadow-inner">
                      <div
                        className={`h-full rounded-full transition-all duration-200 ease-out shadow-sm ${
                          compressing
                            ? 'bg-gradient-to-r from-amber-500 via-orange-400 to-emerald-500'
                            : uploadProgress >= 100
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                            : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500'
                        }`}
                        style={{ width: `${compressing ? compressionProgress : uploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                      {compressing ? (
                        <>
                          <span>Optimizing image resolution & size before upload...</span>
                          <span>{formatBytes(grandTotalBytes)} original</span>
                        </>
                      ) : (
                        <>
                          <span>{formatBytes(uploadedBytesInfo.loaded)} uploaded</span>
                          <span>{formatBytes(uploadedBytesInfo.total || grandTotalBytes)} total</span>
                        </>
                      )}
                    </div>
                    {compressionStats && compressionStats.savedPercent > 0 && (
                      <div className="pt-2 border-t border-blue-200/60 dark:border-blue-800/60 flex items-center justify-between text-xs">
                        <span className="text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1">
                          ⚡ Compressed:
                          <span className="line-through text-slate-400 font-normal">{formatBytes(compressionStats.originalTotal)}</span>
                          <span>➔</span>
                          <span className="font-bold">{formatBytes(compressionStats.compressedTotal)}</span>
                        </span>
                        <span className="bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold text-[11px]">
                          Saved {compressionStats.savedPercent}% ({formatBytes(compressionStats.savedBytes)})
                        </span>
                      </div>
                    )}
                  </div>
                )}

              </div>

              <div className="p-4 sm:p-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isUploading}
                  className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 bg-slate-100 dark:bg-slate-800/80 rounded-xl transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSubCategory}
                  disabled={isUploading}
                  className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-60 flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>
                        {compressing
                          ? `Compressing (${compressionProgress}%)...`
                          : uploadProgress >= 100
                          ? "Processing..."
                          : `Uploading ${uploadProgress}%`}
                      </span>
                    </>
                  ) : (
                    <span>{editingSubCategoryId ? 'Save Changes' : 'Create Sub-Category'}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Gallery Modal */}
      {
        galleryItem && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setGalleryItem(null)}></div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col relative z-10 overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Gallery: {galleryItem.title}</h3>
                  <button
                    onClick={() => addMediaInputRef.current && addMediaInputRef.current.click()}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg shadow-sm transition-all"
                    title="Add Media to this Sub-Category"
                  >
                    <Plus size={14} />
                    <span>Add Media</span>
                  </button>
                </div>
                <button onClick={() => setGalleryItem(null)} className="text-slate-400 hover:text-slate-600 transition-colors p-2 bg-slate-100 rounded-full dark:bg-slate-700">
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {galleryItem.image.split(',').map((mediaItem, index) => {
                    const url = mediaItem.trim();
                    const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}/uploads/${url}`;
                    const isVideoItem = fullUrl.match(/\.(mp4|webm|avi|mpeg|mov|mkv)$/i);
                    return (
                      <div key={index} className="aspect-square bg-slate-100 dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 relative group">
                        {isVideoItem ? (
                          <video src={fullUrl} className="w-full h-full object-cover" controls playsInline />
                        ) : (
                          <img src={fullUrl} alt="media" className="w-full h-full object-cover" />
                        )}

                        <div className="absolute top-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEditGalleryImageClick(url)}
                            className="bg-blue-500 text-white p-1.5 rounded-lg hover:bg-blue-600 shadow-sm"
                            title="Edit Media"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteGalleryImage(url)}
                            className="bg-red-500 text-white p-1.5 rounded-lg hover:bg-red-600 shadow-sm"
                            title="Delete Media"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Hidden File Input for Replacing Gallery Image */}
      <input
        type="file"
        accept="image/*,video/*"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChangeForReplace}
      />

      {/* Hidden File Input for Adding Gallery Media */}
      <input
        type="file"
        multiple
        accept="image/*,video/*"
        ref={addMediaInputRef}
        className="hidden"
        onChange={handleGalleryAddMedia}
      />
    </>
  );
}
